<template>
	<div class="relative flex flex-col gap-1">
		<!-- Chat list items -->
		<template v-if="resultsList.length">
			<TransitionGroup name="items-list">
				<div v-for="item in resultsList" :key="item.messageIndex">
					<ChatListSearchItem :item="item"></ChatListSearchItem>
				</div>
			</TransitionGroup>
		</template>

		<!-- Results empty -->
		<div class="text-center p-5 wrap-anywhere" v-else-if="!isLoading">
			Pro výraz
			<em
				><strong>{{ chatStore.chatListSearchString }}</strong></em
			>
			jsme nenašli žádné položky
		</div>

		<div ref="lazyloadSentinel" class="flex justify-center py-3">
			<Spinner v-if="isLoading" class="size-8 text-primary"></Spinner>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ChatListSearchResultItem } from '@/types/Search'
import Spinner from '@/assets/icons/spinner.svg'
import ChatListSearchItem from './ChatListSearchItem.vue'
import { useChatStore } from '@/stores/chatStore'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { ChannelType, MessageType } from '@/types/Chat'

const chatStore = useChatStore()
const SEARCH_PAGE_SIZE = 20

const resultsList = ref<ChatListSearchResultItem[]>([])
const lastItemTimestamp = ref<string | null>(null)
const isMoreItems = ref<boolean>(true)
const isLoading = ref<boolean>(false)
const observer = ref<IntersectionObserver | null>(null)
const lazyloadSentinel = ref<HTMLElement | null>(null)

watch(
	() => chatStore.chatListSearchString,
	() => {
		// search string changed, show loader, reset current search settings
		// and wait for user to stop typing to send request
		isLoading.value = true
		resetSearch()
		performSearch()
	}
)

onMounted(() => {
	performSearch()
})

let searchRequestId = 0 // request id for aborting outdated request
const performSearch = async () => {
	isLoading.value = true

	try {
		const requestId = ++searchRequestId

		const response = await chatStore.searchChannelList(
			chatStore.chatListSearchString,
			SEARCH_PAGE_SIZE,
			lastItemTimestamp.value
		)

		if (requestId !== searchRequestId) {
			return // abort outdated request
		}

		if (response?.items) {
			// add items to list
			resultsList.value.push(...response.items)

			// update timestamp and isMoreItems
			if (response.lastItemTimestamp) {
				lastItemTimestamp.value = response.lastItemTimestamp
			}

			isMoreItems.value = response.isMoreItems

			if (isMoreItems.value) {
				if (!observer.value) {
					// there are more items on the server but observer is not connected -> connect it
					setupObserver()
				}
			} else {
				// no more values on the server -> disconnect observer
				disconnectObserver()
			}
		}
	} finally {
		isLoading.value = false
	}
}

const setupObserver = () => {
	if (!lazyloadSentinel.value || !isMoreItems.value) return

	disconnectObserver()

	observer.value = new IntersectionObserver(
		(entries) => {
			const entry = entries[0]
			if (entry?.isIntersecting && isMoreItems.value && !isLoading.value) {
				performSearch()
			}
		},
		{ rootMargin: '10%' }
	)

	observer.value.observe(lazyloadSentinel.value)
}

const disconnectObserver = () => {
	if (!observer.value) return
	observer.value.disconnect()
	observer.value = null
}

const resetSearch = () => {
	// delete list, reset timestamp and more items and disconnect observer
	resultsList.value = []
	lastItemTimestamp.value = null
	isMoreItems.value = true
	disconnectObserver()
}

onUnmounted(() => {
	disconnectObserver()
	resetSearch()
})
</script>

<style scoped></style>
